import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AvatarImageWithRetry } from "@/components/ui/avatar-image-with-retry";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMutation } from "@apollo/client/react";
import { LINK_SSO_ACCOUNT, SET_PASSWORD } from "@/graphql/mutations";
import { useSearchParams } from "react-router-dom";
import {
  User as UserIcon,
  Mail,
  Phone,
  Calendar,
  ShieldCheck,
  Lock,
  Check,
  AlertCircle,
  Loader2,
  Gift,
  Building2,
  Crown,
  LogOut,
  MoreVertical,
} from "lucide-react";
import { LeaveOrganizationDialog } from "@/components/dashboard/LeaveOrganizationDialog";
import { getInitials, formatDate, type CurrentUser, type UserOrganization } from "./types";

// Google Identity Services types
interface GoogleConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
}

interface GoogleButtonConfig {
  theme?: string;
  size?: string;
  width?: string;
  text?: string;
}

interface GoogleIdentity {
  accounts: {
    id: {
      initialize: (config: GoogleConfig) => void;
      renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
    };
  };
}

declare const google: GoogleIdentity | undefined;

type ProfileTab = "information" | "verification" | "organizations";

interface ProfileSectionProps {
  currentUser: CurrentUser & {
    ssoVerified?: boolean;
    authMethods?: Array<{
      id: string;
      provider: string;
      hasPassword: boolean;
    }>;
  };
  userLoading: boolean;
  nameValue: string;
  setNameValue: (value: string) => void;
  phoneValue: string;
  setPhoneValue: (value: string) => void;
  updatingProfile: boolean;
  hasProfileChanges: boolean;
  handleUpdateProfile: () => void;
  isOwner?: boolean;
  userOrganizations: UserOrganization[];
  currentOrganizationId: string | null;
  handleLeaveOrganization: (organizationId: string) => Promise<void>;
}

export function ProfileSection({
  currentUser,
  userLoading,
  nameValue,
  setNameValue,
  phoneValue,
  setPhoneValue,
  updatingProfile,
  hasProfileChanges,
  handleUpdateProfile,
  isOwner = false,
  userOrganizations,
  currentOrganizationId,
  handleLeaveOrganization,
}: ProfileSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<ProfileTab>(
    tabParam === "verification" ? "verification" : tabParam === "organizations" ? "organizations" : "information"
  );
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isGoogleInitialized, setIsGoogleInitialized] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [selectedOrgToLeave, setSelectedOrgToLeave] = useState<UserOrganization | null>(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ||
    "787453514477-33m2jvargdpl27nkg8na1e4dmnq67f82.apps.googleusercontent.com";

  // Link SSO mutation
  const [linkSSOAccount, { loading: linkingSSO }] = useMutation(LINK_SSO_ACCOUNT, {
    onCompleted: (data: { linkSSOAccount: { success: boolean; error?: string; creditsGranted?: number } }) => {
      if (data.linkSSOAccount.success) {
        setSuccess(
          data.linkSSOAccount.creditsGranted
            ? `Google account linked! You received ${data.linkSSOAccount.creditsGranted} free credits!`
            : "Google account linked successfully!"
        );
        setError("");
        // Reload to update user data
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setError(data.linkSSOAccount.error || "Failed to link account");
      }
    },
    onError: (err) => {
      setError(err.message || "Failed to link account");
    },
  });

  // Set password mutation
  const [setPasswordMutation, { loading: settingPassword }] = useMutation(SET_PASSWORD, {
    onCompleted: (data: { setPassword: { success: boolean; error?: string } }) => {
      if (data.setPassword.success) {
        setSuccess("Password updated successfully!");
        setError("");
        setPassword("");
        setConfirmPassword("");
        setShowPasswordForm(false);
        // Reload to update user data
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setError(data.setPassword.error || "Failed to set password");
      }
    },
    onError: (err) => {
      setError(err.message || "Failed to set password");
    },
  });

  // Handle tab change
  const handleTabChange = (tab: ProfileTab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    setSearchParams(params);
    setError("");
    setSuccess("");
  };

  // Handle Google credential response
  const handleGoogleCredential = useCallback(async (response: { credential: string }) => {
    setError("");
    setSuccess("");
    await linkSSOAccount({
      variables: {
        input: {
          idToken: response.credential,
        },
      },
    });
  }, [linkSSOAccount]);

  // Initialize Google Sign-In
  useEffect(() => {
    if (activeTab !== "verification") return;

    const initializeGoogle = () => {
      // Must use typeof check to avoid ReferenceError when google is not defined
      if (typeof google !== 'undefined' && google && googleButtonRef.current && !isGoogleInitialized) {
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
        });
        
        google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          width: "300",
          text: "continue_with",
        });
        
        setIsGoogleInitialized(true);
      }
    };

    // Must use typeof check to avoid ReferenceError when google is not defined
    if (typeof google !== 'undefined' && google) {
      initializeGoogle();
    } else {
      const checkGoogle = setInterval(() => {
        if (typeof google !== 'undefined' && google) {
          initializeGoogle();
          clearInterval(checkGoogle);
        }
      }, 100);

      return () => clearInterval(checkGoogle);
    }
  }, [activeTab, handleGoogleCredential, isGoogleInitialized, GOOGLE_CLIENT_ID]);

  // Password validation helper
  const isPasswordValid = (pwd: string) => {
    return pwd.length >= 8 && /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) && /[0-9]/.test(pwd);
  };

  // Handle password submit
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isPasswordValid(password)) {
      setError("Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    await setPasswordMutation({
      variables: {
        input: {
          password,
          confirmPassword,
        },
      },
    });
  };

  // Check if user has Google linked - either via ssoVerified flag or authMethods
  const hasGoogleLinked = currentUser.ssoVerified === true || currentUser.authMethods?.some(m => m.provider === "google");
  const hasPassword = currentUser.authMethods?.some(m => m.provider === "email" && m.hasPassword);

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h2 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">Profile</h2>
        <p className="text-muted-foreground text-lg">Manage your personal information and security</p>
      </div>

      {/* Horizontal Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-8">
          <button
            onClick={() => handleTabChange("information")}
            className={`pb-4 px-1 text-base font-medium border-b-2 transition-colors ${
              activeTab === "information"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
            }`}
          >
            Information
          </button>
          <button
            onClick={() => handleTabChange("verification")}
            className={`pb-4 px-1 text-base font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "verification"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
            }`}
          >
            Verification
            {isOwner && !currentUser.ssoVerified && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">
                +100 credits
              </Badge>
            )}
          </button>
          <button
            onClick={() => handleTabChange("organizations")}
            className={`pb-4 px-1 text-base font-medium border-b-2 transition-colors ${
              activeTab === "organizations"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
            }`}
          >
            Organizations
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "information" ? (
        <div className="space-y-8 animate-fade-in">
          {/* User Avatar and Name */}
          <div className="flex items-center gap-6">
            <Avatar className="w-24 h-24">
              {currentUser.avatarURL && currentUser.avatarURL.trim() !== "" ? (
                <AvatarImageWithRetry 
                  src={currentUser.avatarURL} 
                  alt={currentUser.name || currentUser.email || "User"}
                  maxRetries={1}
                  retryDelay={5000}
                  enableSmartRetry={true}
                />
              ) : null}
              <AvatarFallback className="bg-gradient-hero text-white text-3xl font-semibold">
                {getInitials(currentUser.name || currentUser.email || "User")}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-3xl font-bold">
                {userLoading ? "Loading..." : currentUser.name || currentUser.email || "User"}
              </h3>
              <p className="text-lg text-muted-foreground">
                {userLoading ? "Loading..." : currentUser.email}
              </p>
            </div>
          </div>

          <Separator />

          {/* Personal Information */}
          <div className="space-y-6">
            <h4 className="text-2xl font-bold">Personal Information</h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground text-base">
                  <UserIcon className="w-4 h-4" />
                  Name
                </Label>
                <Input 
                  value={userLoading ? "Loading..." : nameValue} 
                  onChange={(e) => setNameValue(e.target.value)}
                  disabled={userLoading}
                  placeholder="Enter your name"
                  className="h-12 text-base"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground text-base">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <Input 
                  value={userLoading ? "Loading..." : (currentUser.email || "")} 
                  disabled 
                  className="h-12 text-base bg-muted/50 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground text-base">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </Label>
                <Input 
                  value={userLoading ? "Loading..." : phoneValue} 
                  disabled={userLoading}
                  onChange={(e) => setPhoneValue(e.target.value)}
                  placeholder="Enter phone number"
                  className="h-12 text-base"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground text-base">
                  <Calendar className="w-4 h-4" />
                  Account Created
                </Label>
                <Input 
                  value={userLoading ? "Loading..." : formatDate(currentUser.createdAt)} 
                  disabled 
                  className="h-12 text-base bg-muted/50 cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className="flex justify-end">
            <Button
              onClick={handleUpdateProfile}
              disabled={updatingProfile || !hasProfileChanges}
              size="lg"
              className={`h-12 px-8 text-base transition-all ${
                hasProfileChanges 
                  ? 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25' 
                  : 'opacity-50'
              }`}
            >
              {updatingProfile ? "Updating..." : "Update Profile"}
            </Button>
          </div>
        </div>
      ) : activeTab === "verification" ? (
        <div className="space-y-8 animate-fade-in">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-green-500/50 bg-green-500/10">
              <Check className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-green-500">{success}</AlertDescription>
            </Alert>
          )}

          {/* SSO Verification Section */}
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                hasGoogleLinked ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
              }`}>
                <ShieldCheck className={`w-6 h-6 ${
                  hasGoogleLinked ? 'text-green-600' : 'text-blue-600'
                }`} />
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold flex items-center gap-2">
                  Google Account
                  {hasGoogleLinked && (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      <Check className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                  )}
                </h4>
                {hasGoogleLinked ? (
                  <p className="text-muted-foreground mt-1">
                    Your Google account is connected. You can sign in with Google.
                  </p>
                ) : (
                  <>
                    <p className="text-muted-foreground mt-1">
                      Connect your Google account to verify your identity
                      {isOwner && (
                        <> and receive{" "}
                        <span className="font-semibold text-blue-600">100 free streaming credits</span></>
                      )}.
                    </p>
                    <div className="mt-4 flex items-start gap-4">
                      <div className="relative">
                        <div
                          ref={googleButtonRef}
                          className="absolute inset-0 opacity-0 pointer-events-auto z-10"
                          style={{ minHeight: '40px', width: '300px' }}
                        />
                        <Button
                          disabled={linkingSSO}
                          className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 pointer-events-none"
                          variant="outline"
                        >
                          {linkingSSO ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            <>
                              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                              </svg>
                              Connect Google Account
                            </>
                          )}
                        </Button>
                      </div>
                      {isOwner && (
                        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
                          <Gift className="w-4 h-4" />
                          +100 free credits
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Password Section */}
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                hasPassword ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'
              }`}>
                <Lock className={`w-6 h-6 ${
                  hasPassword ? 'text-green-600' : 'text-muted-foreground'
                }`} />
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold flex items-center gap-2">
                  Password
                  {hasPassword && (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      <Check className="w-3 h-3 mr-1" />
                      Set
                    </Badge>
                  )}
                </h4>
                {hasPassword ? (
                  <p className="text-muted-foreground mt-1">
                    You can sign in with your email and password.
                  </p>
                ) : hasGoogleLinked ? (
                  <p className="text-muted-foreground mt-1">
                    You signed up with Google. Set a password to also sign in with your email.
                  </p>
                ) : (
                  <p className="text-muted-foreground mt-1">
                    Set a password for your account.
                  </p>
                )}

                {/* Show Change Password button when password is already set */}
                {hasPassword && !showPasswordForm && (
                  <Button
                    variant="outline"
                    onClick={() => setShowPasswordForm(true)}
                    className="mt-4"
                  >
                    Change Password
                  </Button>
                )}

                {/* Show form when no password OR when toggle is clicked */}
                {(!hasPassword || showPasswordForm) && (
                  <form onSubmit={handlePasswordSubmit} className="mt-4 space-y-4 max-w-md">
                    <div className="space-y-2">
                      <Label htmlFor="password">
                        {hasPassword ? "New Password" : "Password"}
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Create a strong password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={settingPassword}
                        className="h-12"
                      />
                      {/* Password requirements */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-2">
                        <div className={`flex items-center gap-1.5 ${password.length >= 8 ? 'text-green-500' : 'text-muted-foreground'}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${password.length >= 8 ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                          8+ characters
                        </div>
                        <div className={`flex items-center gap-1.5 ${/[A-Z]/.test(password) ? 'text-green-500' : 'text-muted-foreground'}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${/[A-Z]/.test(password) ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                          1 uppercase
                        </div>
                        <div className={`flex items-center gap-1.5 ${/[a-z]/.test(password) ? 'text-green-500' : 'text-muted-foreground'}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${/[a-z]/.test(password) ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                          1 lowercase
                        </div>
                        <div className={`flex items-center gap-1.5 ${/[0-9]/.test(password) ? 'text-green-500' : 'text-muted-foreground'}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${/[0-9]/.test(password) ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                          1 number
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={settingPassword}
                        className="h-12"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        disabled={settingPassword || !isPasswordValid(password) || password !== confirmPassword}
                        className="h-12"
                      >
                        {settingPassword ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            {hasPassword ? "Updating..." : "Setting..."}
                          </>
                        ) : hasPassword ? (
                          "Update Password"
                        ) : (
                          "Set Password"
                        )}
                      </Button>
                      {hasPassword && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setShowPasswordForm(false);
                            setPassword("");
                            setConfirmPassword("");
                            setError("");
                          }}
                          className="h-12"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Organizations Tab */
        <div className="space-y-8 animate-fade-in">
          <div className="space-y-4">
            <h4 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Your Organizations
            </h4>
          </div>

          <div className="space-y-3">
            {userOrganizations.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 border border-dashed border-border rounded-lg">
                You're not a member of any organizations yet.
              </div>
            ) : (
              userOrganizations.map((org) => {
                const isOwnerRole = org.role?.toUpperCase() === "OWNER";
                const isCurrentOrg = org.id === currentOrganizationId;
                
                return (
                  <div
                    key={org.id}
                    className={`flex items-center justify-between p-5 border border-border rounded-lg hover:bg-muted/50 transition-colors ${
                      isCurrentOrg ? 'ring-2 ring-primary/20 bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-hero flex items-center justify-center text-white font-semibold text-lg">
                        {org.name?.charAt(0) || "O"}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-lg">{org.name}</p>
                          {isOwnerRole && (
                            <Crown className="w-4 h-4 text-primary" />
                          )}
                          {isCurrentOrg && (
                            <Badge variant="outline" className="text-xs">
                              Current
                            </Badge>
                          )}
                        </div>
                        <p className="text-base text-muted-foreground">
                          {isOwnerRole ? "Owner" : org.role?.charAt(0).toUpperCase() + org.role?.slice(1).toLowerCase()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge variant={isOwnerRole ? "default" : "secondary"}>
                        {isOwnerRole ? "Owner" : org.role?.charAt(0).toUpperCase() + org.role?.slice(1).toLowerCase()}
                      </Badge>
                      {!isOwnerRole && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrgToLeave(org);
                                setShowLeaveDialog(true);
                              }}
                              className="text-destructive"
                            >
                              <LogOut className="w-4 h-4 mr-2" />
                              Leave Organization
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Leave Organization Confirmation Dialog */}
      <LeaveOrganizationDialog
        open={showLeaveDialog}
        onOpenChange={setShowLeaveDialog}
        onConfirm={async () => {
          if (selectedOrgToLeave) {
            await handleLeaveOrganization(selectedOrgToLeave.id);
          }
        }}
        organizationName={selectedOrgToLeave?.name || ""}
      />
    </div>
  );
}
